import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Heart, Book, MessageCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-cyan-50 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-8">
              You Are Not Alone
            </h1>
            <p className="text-2xl sm:text-3xl text-gray-700 mb-6 font-light">
              Sometimes all you need is{' '}
              <span className="text-teal-700 font-semibold">two sandwiches and a nap</span>
            </p>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
              A place for hope, recovery resources, and support. Whether you're struggling with 
              addiction or supporting someone who is, help is available 24/7.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/resources">
                <Button size="lg" className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg">
                  Find Help Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:988">
                <Button size="lg" variant="destructive" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />
                  Crisis Line: 988
                </Button>
              </a>
            </div>

            <div className="text-sm text-gray-500">
              <Phone className="w-4 h-4 inline mr-2" />
              Free, confidential support available 24 hours a day, 7 days a week
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/resources">
              <Card className="border-2 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Crisis Support</h3>
                  <p className="text-gray-600">
                    Immediate help available 24/7
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/resources">
              <Card className="border-2 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">AA & NA Resources</h3>
                  <p className="text-gray-600">
                    Find meetings and support groups
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/book">
              <Card className="border-2 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Book className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">The Book</h3>
                  <p className="text-gray-600">
                    Read the story of recovery
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/blog">
              <Card className="border-2 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Blog & Stories</h3>
                  <p className="text-gray-600">
                    Hope and inspiration
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Book Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1512820790803-83ca734da794?w=600&q=80"
                alt="Two Sandwiches and a Nap Book"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full">
                Coming Soon
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
                Two Sandwiches and a Nap
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                A personal journey through addiction recovery that offers honest insights, 
                hope, and the reminder that sometimes the simplest acts of self-care are the most powerful.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This book shares real experiences, struggles, and triumphs on the path to recovery. 
                It's a reminder that you don't have to be perfect - you just have to take care of yourself, 
                one day at a time.
              </p>
              <Link to="/book">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg">
                  Learn More About the Book
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Why "Two Sandwiches and a Nap"?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            In the depths of addiction and early recovery, we often forget the basics of taking care 
            of ourselves. This name comes from simple, powerful advice: when everything feels overwhelming 
            and you don't know what to do next...
          </p>
          <p className="text-2xl text-teal-700 font-semibold mb-6">
            Eat two sandwiches. Take a nap. Wake up and try again.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            It's a reminder that recovery doesn't have to be complicated. Sometimes healing starts 
            with the simplest acts of self-compassion and self-care.
          </p>
          <div className="mt-10">
            <Link to="/about">
              <Button variant="outline" size="lg" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50">
                Read More About Our Mission
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Need Help Right Now?
          </h2>
          <p className="text-xl mb-8 text-teal-50">
            You don't have to face this alone. Support is available 24/7, and recovery is possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:988">
              <Button size="lg" className="w-full sm:w-auto bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call 988 Now
              </Button>
            </a>
            <Link to="/resources">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-teal-800 px-8 py-6 text-lg">
                View All Resources
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
